/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { rrulestr } from 'rrule';
import type { CalendarEvent } from '@services/requests/housien-models/CalendarEvent';

/**
 * A single rendered occurrence. Extends the source event with the slot it
 * occupies (`occurrence_start`) and a back-reference to the master event.
 */
export type ExpandedEvent = CalendarEvent & {
  occurrence_start: string;
  master_event_id: string;
  /**
   * Colour to paint this occurrence with, overriding its calendar's.
   *
   * Set for occurrences surfaced by the MEMBROS filter: they are on screen
   * because of a member's row, so they carry that member's colour even when
   * the event itself lives on the institutional calendar. Every chip then
   * matches the row that put it there.
   */
  display_color?: string;
};

/**
 * Whether an event is a recurrence master (carries an RRULE).
 * @param event - The calendar event to test.
 * @returns True when the event has a non-empty recurrence rule.
 */
const isMaster = (event: CalendarEvent): boolean =>
  Array.isArray(event.recurrence) && event.recurrence.length > 0;

/**
 * Whether an event is an override of a single occurrence of a master.
 * @param event - The calendar event to test.
 * @returns True when it references both a master and a specific occurrence.
 */
const isOverride = (event: CalendarEvent): boolean =>
  Boolean(event.recurring_event_id && event.recurrence_id);

/**
 * Whether an ISO timestamp falls within the inclusive `[start, end]` window.
 * @param iso - The timestamp to test.
 * @param start - The inclusive window start.
 * @param end - The inclusive window end.
 * @returns True when the timestamp is inside the window.
 */
const inRange = (iso: string, start: Date, end: Date): boolean => {
  const time = new Date(iso).getTime();
  return time >= start.getTime() && time <= end.getTime();
};

/**
 * Shift the event's end so an occurrence preserves the master's duration.
 * @param event - The source event carrying the original start/end.
 * @param occurrenceStart - The occurrence's start timestamp.
 * @returns The occurrence end ISO timestamp (start + original duration).
 */
const shiftEnd = (event: CalendarEvent, occurrenceStart: string): string => {
  const durationMs =
    new Date(event.end_at_utc).getTime() -
    new Date(event.start_at_utc).getTime();
  return new Date(
    new Date(occurrenceStart).getTime() + durationMs
  ).toISOString();
};

/**
 * Project an event into a concrete occurrence at `occurrenceStart`.
 * @param event - The source (master or single) event.
 * @param occurrenceStart - The occurrence's start timestamp.
 * @returns The expanded occurrence with slot and master back-reference.
 */
const toExpanded = (
  event: CalendarEvent,
  occurrenceStart: string
): ExpandedEvent => ({
  ...event,
  start_at_utc: occurrenceStart,
  end_at_utc: shiftEnd(event, occurrenceStart),
  occurrence_start: occurrenceStart,
  master_event_id: event.recurring_event_id ?? event.event_id,
});

/**
 * Build a set of excluded occurrence timestamps (in ms) for a master.
 * @param event - The master event carrying `excluded_dates`.
 * @returns A set of epoch-ms timestamps to skip during expansion.
 */
const buildExcludedSet = (event: CalendarEvent): Set<number> => {
  const set = new Set<number>();
  (event.excluded_dates ?? []).forEach((iso) => {
    set.add(new Date(iso).getTime());
  });
  return set;
};

/**
 * Index override events by `masterId|occurrenceMs` for fast lookup.
 * @param events - The full event list to scan for overrides.
 * @returns A map from occurrence key to the overriding event.
 */
const buildOverrideMap = (
  events: CalendarEvent[]
): Map<string, CalendarEvent> => {
  const map = new Map<string, CalendarEvent>();
  events.filter(isOverride).forEach((event) => {
    const key = `${event.recurring_event_id}|${new Date(
      event.recurrence_id as string
    ).getTime()}`;
    map.set(key, event);
  });
  return map;
};

/**
 * Whether a specific occurrence of a master has an override.
 * @param master - The master event.
 * @param occurrenceMs - The occurrence start in epoch ms.
 * @param overrides - The override map keyed by `masterId|occurrenceMs`.
 * @returns True when an override replaces this occurrence.
 */
const occurrenceIsReplaced = (
  master: CalendarEvent,
  occurrenceMs: number,
  overrides: Map<string, CalendarEvent>
): boolean => overrides.has(`${master.event_id}|${occurrenceMs}`);

/**
 * Parse a master's recurrence rules into an rrule object.
 * @param master - The master event carrying the RRULE lines.
 * @returns The parsed rrule anchored at the master's start.
 */
const buildRule = (master: CalendarEvent) => {
  const rules = (master.recurrence ?? []).join('\n');
  return rrulestr(rules, { dtstart: new Date(master.start_at_utc) });
};

/**
 * Expand a recurrence master into occurrences within the range, skipping
 * excluded dates and occurrences replaced by an override.
 * @param master - The recurrence master event.
 * @param start - The inclusive range start.
 * @param end - The inclusive range end.
 * @param overrides - The override map for skipping replaced occurrences.
 * @returns The expanded occurrences for the master.
 */
const expandMaster = (
  master: CalendarEvent,
  start: Date,
  end: Date,
  overrides: Map<string, CalendarEvent>
): ExpandedEvent[] => {
  const excluded = buildExcludedSet(master);
  const rule = buildRule(master);
  return rule
    .between(start, end, true)
    .map((date) => date.toISOString())
    .filter((iso) => !excluded.has(new Date(iso).getTime()))
    .filter(
      (iso) =>
        !occurrenceIsReplaced(master, new Date(iso).getTime(), overrides)
    )
    .map((iso) => toExpanded(master, iso));
};

/**
 * Expand a non-recurring (single or override) event, if it is in range.
 * @param event - The single event to place.
 * @param start - The inclusive range start.
 * @param end - The inclusive range end.
 * @returns The single occurrence when in range, otherwise empty.
 */
const expandSingle = (
  event: CalendarEvent,
  start: Date,
  end: Date
): ExpandedEvent[] =>
  inRange(event.start_at_utc, start, end)
    ? [toExpanded(event, event.start_at_utc)]
    : [];

/**
 * Expand a list of calendar events (masters, overrides and single events) into
 * concrete occurrences within `[rangeStart, rangeEnd]`.
 *
 * - Masters with an RRULE are expanded; their `excluded_dates` are skipped and
 *   any occurrence with a matching override is omitted (the override is added
 *   instead, as a single event).
 * - Single events and overrides are included when they fall inside the range.
 */
export const expandEvents = (
  events: CalendarEvent[],
  rangeStart: Date,
  rangeEnd: Date
): ExpandedEvent[] => {
  const overrides = buildOverrideMap(events);
  const result: ExpandedEvent[] = [];
  events.forEach((event) => {
    if (isMaster(event)) {
      result.push(...expandMaster(event, rangeStart, rangeEnd, overrides));
      return;
    }
    result.push(...expandSingle(event, rangeStart, rangeEnd));
  });
  return result;
};
